import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Database,
  FileText,
  FlaskConical,
  LayoutGrid,
  Radar,
  Radio,
  RefreshCw,
  Scale,
  Link2,
  Shield,
  ShieldCheck,
  Share2,
  Spline,
  Target,
  Waves,
  Workflow,
  Zap,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const FUSION_STORAGE_KEY = 'aerta_sidebar_fusion_open';
const FUSION_PATHS = ['/operations', '/fusion/cyber-range', '/autonomous-pipeline'];

function isNavActive(pathname, itemPath) {
  if (pathname === itemPath) return true;
  return pathname.startsWith(`${itemPath}/`);
}

function isFusionSectionActive(pathname) {
  return FUSION_PATHS.some((p) => isNavActive(pathname, p));
}

/**
 * Grouped nav — logical flow: start → fusion → discover → intel → enterprise → operate → evidence.
 */
const NAV_SECTIONS = [
  {
    id: 'start',
    title: 'Start',
    items: [
      { path: '/dashboard', icon: BarChart3, label: 'Overview' },
      { path: '/devguard', icon: Shield, label: 'DevGuard' },
    ],
  },
  {
    id: 'discover',
    title: 'Discover & assess',
    items: [
      { path: '/network-scan', icon: Radio, label: 'Network Scan' },
      { path: '/assets', icon: Database, label: 'Assets' },
      { path: '/risk', icon: Radar, label: 'Risk' },
    ],
  },
  {
    id: 'intel',
    title: 'Intelligence',
    items: [
      { path: '/graph-findings', icon: Share2, label: 'Graph Findings' },
      { path: '/registry', icon: Spline, label: 'Registry' },
      { path: '/forecasts', icon: Waves, label: 'Forecasts' },
    ],
  },
  {
    id: 'enterprise',
    title: 'Enterprise & trust',
    items: [
      { path: '/critical-infrastructure', icon: Building2, label: 'Critical Infrastructure' },
      { path: '/compliance', icon: Scale, label: 'Compliance' },
    ],
  },
  {
    id: 'operate',
    title: 'Run & remediate',
    items: [
      { path: '/runs', icon: ShieldCheck, label: 'Executor runs' },
      { path: '/remediation', icon: ShieldCheck, label: 'Remediation' },
      { path: '/verification-loop', icon: Link2, label: 'Verification Loop' },
    ],
  },
  {
    id: 'evidence',
    title: 'Evidence & replay',
    items: [
      { path: '/reports', icon: FileText, label: 'Reports' },
      { path: '/resimulation', icon: RefreshCw, label: 'Resimulation' },
      { path: '/events', icon: FlaskConical, label: 'Events' },
    ],
  },
];

const fusionChildren = [
  { path: '/operations', icon: LayoutGrid, label: 'Fusion hub' },
  { path: '/fusion/cyber-range', icon: Target, label: 'Cyber Range' },
  { path: '/autonomous-pipeline', icon: Workflow, label: 'Autonomous pipeline' },
];

/** All page links in sidebar order (Overview first). */
const FLAT_NAV = NAV_SECTIONS.flatMap((s) => s.items);

function NavItem({ item, collapsed, locationPathname }) {
  const isActive = isNavActive(locationPathname, item.path);
  return (
    <NavLink
      to={item.path}
      title={collapsed ? item.label : undefined}
      className={cn(
        'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-all duration-200 ease-out',
        collapsed && 'justify-center px-2',
        isActive
          ? 'border border-primary/25 bg-primary/10 text-primary shadow-sm shadow-primary/10'
          : 'text-muted-foreground hover:translate-x-0.5 hover:bg-secondary hover:text-foreground'
      )}
    >
      <item.icon className={cn('h-5 w-5 flex-shrink-0', isActive && 'text-primary')} />
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );
}

function SectionLabel({ children }) {
  return (
    <p className="mb-1 mt-3 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/90 first:mt-0">
      {children}
    </p>
  );
}

export function Sidebar({ collapsed, onToggle }) {
  const location = useLocation();
  const [fusionOpen, setFusionOpen] = useState(() => {
    try {
      return window.localStorage.getItem(FUSION_STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (isFusionSectionActive(location.pathname)) {
      setFusionOpen(true);
    }
  }, [location.pathname]);

  const toggleFusion = useCallback(() => {
    setFusionOpen((v) => {
      const next = !v;
      try {
        window.localStorage.setItem(FUSION_STORAGE_KEY, next ? 'true' : 'false');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const renderFusion = () =>
    collapsed ? (
      <NavLink
        to="/operations"
        title="Fusion command"
        className={cn(
          'flex items-center justify-center rounded-md px-3 py-2.5 text-sm font-medium transition-all duration-200 ease-out',
          isFusionSectionActive(location.pathname)
            ? 'border border-primary/25 bg-primary/10 text-primary shadow-sm shadow-primary/10'
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
        )}
      >
        <Zap className={cn('h-5 w-5 flex-shrink-0', isFusionSectionActive(location.pathname) && 'text-primary')} />
      </NavLink>
    ) : (
      <div className="rounded-md border border-border/50 bg-secondary/20">
        <button
          type="button"
          onClick={toggleFusion}
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm font-semibold transition-colors',
            isFusionSectionActive(location.pathname) ? 'text-primary' : 'text-foreground hover:bg-secondary/80'
          )}
        >
          <Zap className="h-5 w-5 shrink-0 text-primary" />
          <span className="flex-1">Fusion command</span>
          {fusionOpen ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
        </button>
        {fusionOpen ? (
          <div className="border-t border-border/40 pb-1 pt-0.5">
            {fusionChildren.map((child) => {
              const isActive = isNavActive(location.pathname, child.path);
              return (
                <NavLink
                  key={child.path}
                  to={child.path}
                  className={cn(
                    'flex items-center gap-2 rounded-md py-2 pl-9 pr-2 text-sm font-medium transition-all duration-200 ease-out',
                    isActive
                      ? 'border border-primary/20 bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-secondary/90 hover:text-foreground'
                  )}
                >
                  <child.icon className={cn('h-4 w-4 flex-shrink-0', isActive && 'text-primary')} />
                  <span>{child.label}</span>
                </NavLink>
              );
            })}
          </div>
        ) : null}
      </div>
    );

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen border-r border-border bg-background/95 backdrop-blur-sm transition-all duration-300 ease-out-expo',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex h-16 items-center justify-between border-b border-border px-4">
        {!collapsed ? (
          <div className="flex items-center gap-2">
            <Shield className="h-8 w-8 text-primary" />
            <span className="font-heading text-xl font-bold text-foreground">AERTA</span>
          </div>
        ) : (
          <Shield className="mx-auto h-8 w-8 text-primary" />
        )}
        <button
          onClick={onToggle}
          type="button"
          className="rounded-md p-1 transition-colors hover:bg-secondary"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronLeft className="h-4 w-4 text-muted-foreground" />}
        </button>
      </div>

      <nav className="flex max-h-[calc(100vh-8rem)] flex-col gap-0.5 overflow-y-auto p-3 pb-24">
        {collapsed ? (
          <>
            {NAV_SECTIONS[0].items.map((item) => (
              <NavItem key={item.path} item={item} collapsed locationPathname={location.pathname} />
            ))}
            {renderFusion()}
            {FLAT_NAV.slice(NAV_SECTIONS[0].items.length).map((item) => (
              <NavItem key={item.path} item={item} collapsed locationPathname={location.pathname} />
            ))}
          </>
        ) : (
          <>
            <SectionLabel>Start</SectionLabel>
            {NAV_SECTIONS[0].items.map((item) => (
              <NavItem key={item.path} item={item} collapsed={false} locationPathname={location.pathname} />
            ))}
            <SectionLabel>Command</SectionLabel>
            {renderFusion()}
            {NAV_SECTIONS.slice(1).map((section) => (
              <React.Fragment key={section.id}>
                <SectionLabel>{section.title}</SectionLabel>
                {section.items.map((item) => (
                  <NavItem key={item.path} item={item} collapsed={false} locationPathname={location.pathname} />
                ))}
              </React.Fragment>
            ))}
          </>
        )}
      </nav>

      {!collapsed && (
        <div className="absolute bottom-4 left-4 right-4 rounded-md border border-border bg-card p-3">
          <p className="text-xs text-muted-foreground">Pilot Mode</p>
          <p className="text-xs font-mono text-primary">AERTA v3 readiness</p>
        </div>
      )}
    </aside>
  );
}
